import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { armazenamento } from './armazenamento.ts';

/**
 * H-304 — o que o estudante já pagou, guardado no aparelho.
 *
 * A inscrição vive em `enrollments` e é o servidor que a decide (ADR 0021:
 * «o acesso vem da inscrição, a inscrição vem do pagamento»). O que está aqui
 * é a **cópia** para o selo «Pago» se desenhar no catálogo sem esperar por
 * rede — como a identidade presumida. Escrever aqui à mão não abre nada:
 * nada neste ficheiro decide se uma lição abre; decide só se o cartão diz
 * «já tens acesso».
 *
 * Lida a seguir à sonda de sessão, e limpa com a identidade.
 */

export interface Inscricao {
  oferta: string;
  nome: string;
  ambito: string;
  alvo: { curso?: string; percurso?: string; nivel?: string } | null;
  desde: string;
  ate: string | null;
}

interface EstadoDeAcessos {
  inscricoes: Inscricao[];
  subscricao: { plano: string; ate: string } | null;
  definir: (dados: { inscricoes: Inscricao[]; subscricao: { plano: string; ate: string } | null }) => void;
  limpar: () => void;
}

export const useAcessos = create<EstadoDeAcessos>()(
  persist(
    (set) => ({
      inscricoes: [],
      subscricao: null,
      definir: (dados) => set({ inscricoes: dados.inscricoes, subscricao: dados.subscricao }),
      limpar: () => set({ inscricoes: [], subscricao: null }),
    }),
    { name: 'acessos', storage: armazenamento() },
  ),
);

/** O curso está pago: uma inscrição de curso, ou a subscrição, que abre tudo. */
export function cursoPago(estado: EstadoDeAcessos, curso: string): boolean {
  if (estado.subscricao) return true;
  return estado.inscricoes.some((i) => i.alvo?.curso === curso);
}

/** O percurso inteiro está pago (oferta de percurso completo, ou subscrição). */
export function percursoPago(estado: EstadoDeAcessos, percurso: string): boolean {
  if (estado.subscricao) return true;
  return estado.inscricoes.some((i) => i.ambito === 'path' && i.alvo?.percurso === percurso);
}

/** O nível está pago: pelo próprio nível, pelo percurso completo, ou pela subscrição. */
export function nivelPago(estado: EstadoDeAcessos, percurso: string, nivel: string): boolean {
  if (percursoPago(estado, percurso)) return true;
  return estado.inscricoes.some((i) => i.alvo?.nivel === nivel && i.alvo.percurso === percurso);
}

/** Sonda de acessos: corre a seguir à de sessão, só com conta, e falha em silêncio. */
export async function sondarAcessos(): Promise<void> {
  try {
    const resposta = await fetch('/api/inscricoes', { credentials: 'same-origin' });
    if (resposta.status === 401) {
      useAcessos.getState().limpar();
      return;
    }
    if (!resposta.ok) return;
    const dados = (await resposta.json()) as {
      inscricoes?: Inscricao[];
      subscricao?: { plano: string; ate: string } | null;
    };
    useAcessos.getState().definir({
      inscricoes: Array.isArray(dados.inscricoes) ? dados.inscricoes : [],
      subscricao: dados.subscricao ?? null,
    });
  } catch {
    // Offline: a cópia guardada é a resposta.
  }
}
